/**
 * Forge list: create (name → draft arche + forge, started straight away),
 * status chips, start/stop, delete, open — and, below your own, the test builds of forges SHARED with you
 * (arches you are an active collaborator on; ADR-023 Phase 3). Shared entries
 * are read-only: "Open test version" is the only thing a collaborator can do
 * with someone else's forge.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Download,
  ExternalLink,
  Hammer,
  Info,
  Loader2,
  Play,
  Plus,
  Square,
  Trash2,
  Users,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { useForges } from '../hooks/useForges';
import { useForgeAvailability } from '../hooks/useForgeAvailability';
import { forgeDeletedToast, useDeleteForge } from '../hooks/useDeleteForge';
import { forgeDisplayName } from '../components/ForgeNameEditor';
import { ForgeDeleteDialog } from '../components/ForgeDeleteDialog';
import { useToast } from '@/components/ui/use-toast';
import { FORGE_SIZE_OPTIONS, ForgeStatusChip } from '../components/ForgeStatusBar';
import type { ForgeSize, ForgeSummary } from '../types';
import { ForgeUsagePanel } from '../components/ForgeUsagePanel';
import { testBuildUrl } from '../components/ForgeTestBuilds';

import { FORGE_ARCHE_ID } from '../constants';

export { FORGE_ARCHE_ID };

export function ForgeListPage() {
  const navigate = useNavigate();
  const {
    forges,
    shared,
    isLoading,
    error,
    createForge,
    isCreating,
    importFromPlugin,
    isImporting,
    startForge,
    stopForge,
    pendingForgeId,
  } = useForges({ includeShared: true });
  const { deleteForge, deletingForgeId } = useDeleteForge();
  // The kill switch (ADR-023): while Fias Forge is off, nothing new can be
  // made here — the server refuses it — so the forms are not offered. Existing
  // forges stay listed so their owners can still delete them.
  const availability = useForgeAvailability();
  const closed = !availability.isLoading && !availability.offered;
  // Neither the forms nor the notice until the switch is known — no flash of
  // forms that then disappear.
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [size, setSize] = useState<ForgeSize>('standard');
  const [importArcheId, setImportArcheId] = useState('');
  const [forgeToDelete, setForgeToDelete] = useState<ForgeSummary | null>(null);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    let forge: ForgeSummary;
    try {
      // `standard` is the server default; only a non-default size travels.
      forge = await createForge(trimmed, size === 'standard' ? undefined : size);
    } catch {
      // Surfaced through `error` from the hook.
      return;
    }
    setName('');
    // Start it straight away, as the new-arche chooser does: a new forge
    // exists to be worked in, and a stopped one ignores the prompt box. A
    // refused start (plan, monthly hours, running cap) still opens the page,
    // which shows the reason and a Start button.
    try {
      await startForge(forge.id);
    } catch (err) {
      toast({
        title: 'Your forge was created but did not start',
        description: err instanceof Error ? err.message : 'You can start it from its page.',
      });
    }
    navigate(`/a/${FORGE_ARCHE_ID}/${forge.id}`);
  };

  const handleDelete = async (forgeId: string) => {
    const outcome = await deleteForge(forgeId);
    setForgeToDelete(null);
    toast(forgeDeletedToast(outcome));
  };

  // Import a plugin you published with the CLI (ADR-023 D6): the last
  // submitted source becomes the forge's starting point. The CLI keeps
  // working for that arche — nothing moves, unlike a builder import.
  const handleImport = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = importArcheId.trim();
    if (!trimmed) return;
    try {
      const forge = await importFromPlugin(trimmed);
      setImportArcheId('');
      navigate(`/a/${FORGE_ARCHE_ID}/${forge.id}`);
    } catch {
      // Surfaced through `error` from the hook.
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold">Fias Forge</h1>
          <Link
            to={`/marketplace/arches/${FORGE_ARCHE_ID}`}
            className="text-muted-foreground hover:text-primary transition-colors"
            title="View arche details"
          >
            <Info className="h-6 w-6" />
          </Link>
          <FavoriteButton archeId={FORGE_ARCHE_ID} />
        </div>
        <p className="text-muted-foreground mt-1">
          A hosted Linux build environment in which Claude Code builds one arche for you.
        </p>
        {closed ? (
          <p
            role="status"
            data-testid="forge-unavailable"
            className="mt-4 rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
          >
            Fias Forge isn&apos;t available yet. You can keep building arches in the Arche Builder.
          </p>
        ) : availability.offered ? (
          <>
            <form onSubmit={handleCreate} className="flex flex-wrap gap-2 mt-4">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name your new arche"
                maxLength={80}
                aria-label="New arche name"
                className="max-w-xs"
                disabled={isCreating}
              />
              <select
                value={size}
                onChange={(e) => setSize(e.target.value as ForgeSize)}
                aria-label="New forge size"
                title={FORGE_SIZE_OPTIONS.find((o) => o.value === size)?.hint}
                className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                disabled={isCreating}
              >
                {FORGE_SIZE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Button type="submit" disabled={isCreating || !name.trim()}>
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                New forge
              </Button>
            </form>
            <form
              onSubmit={handleImport}
              className="flex flex-wrap items-center gap-2 mt-3"
              aria-label="Import a published plugin"
            >
              <Input
                value={importArcheId}
                onChange={(e) => setImportArcheId(e.target.value)}
                placeholder="arc_… id of a plugin you published"
                maxLength={128}
                aria-label="Published plugin arche id"
                className="max-w-xs font-mono text-xs"
                disabled={isImporting}
              />
              <Button
                type="submit"
                variant="outline"
                disabled={isImporting || !importArcheId.trim()}
                title="Start a forge from the last source you submitted for this plugin. The CLI keeps working for it."
              >
                {isImporting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Import a published plugin
              </Button>
            </form>
          </>
        ) : null}
      </div>

      <ForgeUsagePanel />

      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : forges.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Hammer className="h-10 w-10 mx-auto mb-3 opacity-50" />
            No forges yet. Name an arche above to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {forges.map((forge) => {
            const busy = pendingForgeId === forge.id || deletingForgeId === forge.id;
            const canStart = forge.status === 'stopped' || forge.status === 'failed';
            const canStop = forge.status === 'running' || forge.status === 'provisioning';
            return (
              <Card key={forge.id} data-testid="forge-card">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base truncate" title={forge.archeId}>
                      {forgeDisplayName(forge)}
                    </CardTitle>
                    <ForgeStatusChip status={forge.status} />
                  </div>
                  <CardDescription>
                    {forge.lastActiveAt
                      ? `Active ${formatDistanceToNow(new Date(forge.lastActiveAt), { addSuffix: true })}`
                      : forge.createdAt
                        ? `Created ${formatDistanceToNow(new Date(forge.createdAt), { addSuffix: true })}`
                        : ''}
                    {forge.failureReason ? ` · ${forge.failureReason}` : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate(`/a/${FORGE_ARCHE_ID}/${forge.id}`)}>
                    Open
                  </Button>
                  {canStart && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void startForge(forge.id)}
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      Start
                    </Button>
                  )}
                  {canStop && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void stopForge(forge.id)}
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Square className="h-4 w-4 mr-1" />
                      )}
                      Stop
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-muted-foreground hover:text-destructive"
                    disabled={busy}
                    onClick={() => setForgeToDelete(forge)}
                    aria-label={`Delete forge ${forgeDisplayName(forge)}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ForgeDeleteDialog
        forge={forgeToDelete}
        onCancel={() => setForgeToDelete(null)}
        onConfirm={handleDelete}
      />

      {shared.length > 0 && (
        <section className="mt-10" aria-labelledby="shared-test-builds-heading">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h2 id="shared-test-builds-heading" className="text-xl font-semibold">
              Shared test builds
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Forges of arches you collaborate on. You can open their current test build; only the
            forge&apos;s owner can build, start or publish.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {shared.map((forge) => (
              <Card key={forge.id} data-testid="shared-forge-card">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base truncate" title={forge.archeId}>
                      {forgeDisplayName(forge)}
                    </CardTitle>
                    <ForgeStatusChip status={forge.status} />
                  </div>
                  <CardDescription>
                    {forge.currentTestBuildId
                      ? 'A test build is ready'
                      : 'No test build yet — ask the forge owner to build for testing'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {forge.currentTestBuildId ? (
                    <Button size="sm" variant="outline" asChild>
                      <a href={testBuildUrl(forge.archeId)} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open test version
                      </a>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled>
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open test version
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
